import type { Currency } from '../../constants';
import type { Product } from '../product/product';

export interface CartRewardProductsQueryParams {
  currency: Currency;
  actorId?: string;
}

/** Frontend reward values, independent of an endpoint or transport response. */
export type CartReward =
  | { type: 'free_shipping'; minAmount: number; shippingFee: number }
  | {
      type: 'discount_tier';
      couponCode: string;
      label: string;
      minAmount: number;
      discountType: 'percent';
      percentage: number;
    }
  | {
      type: 'discount_tier';
      couponCode: string;
      label: string;
      minAmount: number;
      discountType: 'fixed_cart';
      discountAmount: number;
    };

export interface CartRewardProduct {
  product: Product;
  reviewStatistics: { averageRate: number; totalReviews: number } | null;
  projection: {
    sku: string;
    quantity: 1;
    /** Selling price before the additional reward. */
    price: number;
    /** Supplied incremental reward price, already floored at zero; null means no reduction. */
    priceAfterReward: number | null;
    /** Display-only amount derived from current cart progress for Almost There. */
    remainingAfterAdd?: number;
  };
}

export type CartRewardGroup =
  | { key: string; type: 'unlocks'; reward: CartReward; products: CartRewardProduct[] }
  | { key: string; type: 'almost_there'; products: CartRewardProduct[] };

/** Server-selected products in display order. No client-side tier or price calculation. */
export interface CartRewardProducts {
  currency: Currency;
  groups: Array<
    | {
        key: string;
        type: 'unlocks';
        reward: Exclude<CartReward, { type: 'free_shipping' }>;
        products: CartRewardProduct[];
      }
    | { key: string; type: 'almost_there'; products: CartRewardProduct[] }
  >;
}

/** Optional current-cart presentation input. This is not a new API contract.
 * Absent input means server-dependent rewards are unavailable.
 */
export interface CartDiscoveryPresentation {
  currency: Currency;
  /** Must match getCartDiscoverySnapshotKey for the currently displayed cart and identity. */
  cartSnapshotKey: string;
  groups: CartRewardGroup[];
}
